import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterCategory } from '../models/helpCenterCategory';
import { HelpCenterSearchResult } from '../models/helpCenterSearchResult';
import { getHelpCenterCategories, searchHelpCenter } from '../services/helpCenterService';
import { getSearchWords, highlightWords } from '../functions/highlightWords';
import { HelpCenterLayout } from '../components/helpCenterLayout';

export const HelpSearchPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') ?? '').trim();
  // Som API:t: ord kortare än två tecken räknas inte.
  const hasWords = getSearchWords(query).length > 0;

  const [categories, setCategories] = useState<HelpCenterCategory[]>([]);
  const [results, setResults] = useState<HelpCenterSearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);

  // Kategorierna visar var varje träff ligger och hämtas en gång. Utan dem visas träffarna ändå, bara utan kategori.
  useEffect(() => {
    let cancelled = false;
    getHelpCenterCategories()
      .then((result) => {
        if (!cancelled) setCategories(result.data);
      })
      .catch((e) => ConsoleHelper.log(e));
    return () => {
      cancelled = true;
    };
  }, []);

  // Fel visas av interceptorn och som en egen text. De förra träffarna står kvar medan nya hämtas, så att listan inte
  // blinkar när man skriver, och ett sent svar för en sökning man redan skrivit vidare på skriver inte över den nya.
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (!hasWords) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    const load = async () => {
      setLoading(true);
      try {
        const result = await searchHelpCenter(query);
        if (!cancelled) setResults(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) {
          setResults([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [query, hasWords]);

  const categoryName = (id: number) => categories.find((x) => x.id === id)?.name ?? '';

  return (
    <HelpCenterLayout trail={[{ key: 'search', label: t('help:searchResults') }]} searchValue={query}>
      <Box sx={{ minHeight: loading ? '10rem' : undefined }}>
        <LoadingOverlay loading={loading} />
        <MainContentHeader title={t('help:searchResults')} />
        {!hasWords && <Typography color='text.secondary'>{t('help:searchTooShort')}</Typography>}
        {hasWords && failed && <Typography color='text.secondary'>{t('help:loadFailed')}</Typography>}
        {hasWords && !loading && !failed && results.length === 0 && (
          <Typography color='text.secondary'>{t('help:searchNoHits', { query })}</Typography>
        )}
        <List>
          {results.map((result) => (
            <ListItemButton
              key={result.slug}
              component={Link}
              to={`${RoutesEnum.HelpArticle}/${encodeURIComponent(result.slug)}`}
              divider
              sx={{ display: 'block', py: 1.5 }}
            >
              <Typography sx={{ fontWeight: 600 }}>{highlightWords(result.title, query)}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {categoryName(result.categoryId)}
              </Typography>
              {result.snippet && (
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  {highlightWords(result.snippet, query)}
                </Typography>
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </HelpCenterLayout>
  );
};
